import React, { useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, QrCode, Printer, X } from 'lucide-react';
import { Button } from './ui/button';
import { useSettings } from '../context/SettingsContext';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';

/**
 * DriverInviteQR — a compact tile (printable) that shows a QR code linking to
 * the driver portal sign-in / signup page. Designed for the Admin Drivers page:
 * the admin can hand the phone to a new driver, who scans the QR with their
 * iPhone camera and lands directly on /driver/login (so iOS Safari snapshots
 * the *driver* page title and apple-touch-icon for Add-to-Home-Screen).
 */
const DriverInviteQR = ({ variant = 'inline' }) => {
  const { toast } = useToast();
  const { settings } = useSettings();
  const { authHeaders } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Admin-controllable QR size (px). Falls back to the factory default 240.
  const qrSize = Math.max(120, Math.min(480, Number(settings?.card_settings?.driver_card?.qr_size_px) || 240));

  // Always use the production-facing URL the user actually scans from a phone.
  // window.location.origin would resolve to the preview URL when admins are
  // logged into the admin panel via preview — which is fine for testing.
  const url = `${window.location.origin}/driver/login`;
  const signupUrl = `${window.location.origin}/driver/signup`;

  const copy = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(true);
      toast({ title: 'Link copied' });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: 'Copy failed', description: 'Long-press the link to copy manually.' });
    }
  };

  // Print uses the admin-customized backend PDF (`build_driver_card_pdf`)
  // so font sizes / margins / QR size / visibility toggles from
  // Admin → Settings → Documents → "Driver onboarding card" all apply.
  // We open the resulting PDF blob in a hidden iframe and trigger the
  // browser's native print dialog — same UX as before, but real,
  // customizable output.
  const print = async () => {
    setPrinting(true);
    try {
      const r = await axios.get(`${API}/admin/driver-card-pdf`, {
        params: { url },
        headers: authHeaders,
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      let iframe = document.getElementById('mr-print-frame');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'mr-print-frame';
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
        document.body.appendChild(iframe);
      }
      iframe.src = blobUrl;
      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (err) {
            toast({ title: 'Print failed', description: err?.message || '' });
          }
        }, 200);
      };
    } catch (err) {
      toast({ title: 'Print failed', description: err.response?.data?.detail || 'Could not generate the printable card.' });
    } finally {
      setPrinting(false);
    }
  };

  if (variant === 'button-only') {
    return (
      <>
        <Button
          onClick={() => setOpen(true)}
          variant="outline"
          className="rounded-full"
          data-testid="driver-qr-open-btn"
        >
          <QrCode className="w-4 h-4 mr-1.5" /> Onboarding QR
        </Button>
        {open && <QRModal {...{ open, setOpen, url, signupUrl, copy, copied, print, printing, qrSize }} />}
      </>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="group inline-flex items-center gap-3 bg-white border border-neutral-200 rounded-2xl px-3 py-2 hover:border-[#0078D4] hover:shadow-md transition"
        data-testid="driver-qr-open-btn"
        title="Show driver onboarding QR code"
      >
        <div className="w-10 h-10 rounded-lg bg-[#0F172A] flex items-center justify-center group-hover:bg-[#0078D4] transition">
          <QrCode className="w-5 h-5 text-white" />
        </div>
        <div className="text-left">
          <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">Onboard</div>
          <div className="text-sm font-semibold text-[#0F172A] -mt-0.5">New driver QR</div>
        </div>
      </button>
      {open && <QRModal {...{ open, setOpen, url, signupUrl, copy, copied, print, printing, qrSize }} />}
    </>
  );
};

const QRModal = ({ setOpen, url, signupUrl, copy, copied, print, printing, qrSize }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" data-testid="driver-qr-modal">
    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
    <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white p-6 relative">
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition"
          aria-label="Close"
          data-testid="driver-qr-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-200">Driver onboarding</div>
        <h2 className="font-display text-2xl mt-1 leading-tight">Scan to install the Driver Portal</h2>
        <p className="text-sm text-emerald-100/90 mt-1.5 leading-relaxed">
          Hand the phone to your driver — they scan with the iPhone Camera app to land directly on the driver sign-in page.
        </p>
      </div>
      <div className="p-6">
        <div className="rounded-2xl bg-slate-50 p-5 flex items-center justify-center">
          <QRCodeSVG
            value={url}
            size={qrSize}
            level="M"
            includeMargin={false}
            data-testid="driver-qr-svg"
            bgColor="#F8FAFC"
            fgColor="#0F172A"
          />
        </div>
        <div className="mt-4 flex items-center gap-2 bg-slate-100 rounded-lg p-2 pl-3">
          <code className="text-xs text-slate-700 truncate flex-1" data-testid="driver-qr-url">{url}</code>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => copy(url)}
            className="h-8 px-2"
            data-testid="driver-qr-copy"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            onClick={print}
            disabled={printing}
            variant="outline"
            className="rounded-full"
            data-testid="driver-qr-print"
          >
            <Printer className="w-4 h-4 mr-1.5" /> {printing ? 'Preparing…' : 'Print card'}
          </Button>
          <Button
            onClick={() => copy(signupUrl)}
            variant="outline"
            className="rounded-full"
            data-testid="driver-qr-copy-signup"
          >
            <Copy className="w-4 h-4 mr-1.5" /> Copy signup link
          </Button>
        </div>
        <div className="mt-5 text-xs text-neutral-600 leading-relaxed bg-amber-50 border border-amber-200 rounded-lg p-3">
          <b className="text-amber-900">iPhone install tip:</b> after scanning, the driver should open in Safari, tap <b>Share → Add to Home Screen</b>, and confirm the suggested name reads <b>"MR Driver"</b> before tapping Add. If it still shows the storefront name, pull down to refresh the page first.
        </div>
      </div>
    </div>
  </div>
);

export default DriverInviteQR;
